/**
 * Rapport du diagnostic d'un enfant, côté parent (`/parent/diagnostic/$id`,
 * migration 0325) : lecture seule — maîtrise, rapport, cartes à travailler.
 */

import { Link } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import { ArrowLeft } from 'lucide-react';
import type { SupabaseClient } from '@supabase/supabase-js';
import { supabase } from '@/lib/supabase';
import { DiagnosticResultView } from './diagnostic-page';

const db = supabase as unknown as SupabaseClient;

export function ParentDiagnosticPage({
  diagnosticId,
  backTo = '/parent/news',
}: {
  diagnosticId: string;
  /** Feed d'où l'on vient : parent, ou tuteur/admin (`/tutor/diagnostic/$id`). */
  backTo?: '/parent/news' | '/tutor/news';
}) {
  // Prénom de l'enfant (RLS parent_select de 0325 + lecture du profil de l'enfant lié).
  const { data: childName } = useQuery({
    queryKey: ['parent-diagnostic-child', diagnosticId],
    queryFn: async (): Promise<string | null> => {
      const { data } = await db
        .from('skill_diagnostics')
        .select('student:profiles!skill_diagnostics_student_id_fkey(display_name:visible_name)')
        .eq('id', diagnosticId)
        .maybeSingle();
      const name = (data as { student: { display_name: string | null } | null } | null)?.student
        ?.display_name;
      return name?.trim().split(' ')[0] || null;
    },
  });

  return (
    <div className="mx-auto max-w-lg px-4 py-6 pb-24 sm:py-8 lg:max-w-2xl">
      <Link
        to={backTo}
        className="text-primary-600 mb-4 inline-flex min-h-[2.75rem] items-center gap-1 text-sm font-semibold"
      >
        <ArrowLeft className="h-4 w-4" aria-hidden /> Feed
      </Link>
      <h1 className="font-display text-ink-900 mb-4 text-2xl font-extrabold">
        Diagnostic{' '}
        {childName ? `de ${childName}` : backTo === '/parent/news' ? 'de votre enfant' : ''}
      </h1>
      <DiagnosticResultView diagnosticId={diagnosticId} audience="parent" />
    </div>
  );
}
